import fileService from '@/services/file.js';
import { getOfflineFile } from '@/services/offlineFiles';

export default {
	namespaced: true,
	state: {
		fileUploadSuccess: null,
		file: null,
		files: [],
		roundImages: [],
		searchParams: {
			search: '',
			orderBy: 'Created',
			pageSize: 10,
			currentPage: 1
		},
		totalCount: 0,
		totalPages: 0
	},
	mutations: {
		SetFileUploadSuccess(state, isSuccess) {
			state.fileUploadSuccess = isSuccess;
		},
		setFile(state, file) {
			state.file = file;
		},
		SET_FILES(state, files) {
			state.files = files;
		},
		SET_PAGESIZE(state, pageSize) {
			state.searchParams.pageSize = pageSize;
		},
		SET_CURRENTPAGE(state, currentPage) {
			state.searchParams.currentPage = currentPage;
		},
		SET_TOTALPAGES(state, totalPages) {
			state.totalPages = totalPages;
		},
		SET_TOTALCOUNT(state, totalCount) {
			state.totalCount = totalCount;
		},
		AddRoundImage(state, { fileId, img, isObjectUrl = false }) {
			const idx = state.roundImages.findIndex(x => x.fileId === fileId);

			// If replacing an old object URL, revoke it
			if (idx !== -1) {
			const prev = state.roundImages[idx];
			if (prev?.isObjectUrl && typeof prev.img === 'string' && prev.img.startsWith('blob:')) {
				try { URL.revokeObjectURL(prev.img); } catch (e) {}
			}
			state.roundImages.splice(idx, 1, { fileId, img, isObjectUrl });
			} else {
			state.roundImages.push({ fileId, img, isObjectUrl });
			}
		},
		ResetRoundImages(state) {
			// Revoke all blob URLs before clearing
			(state.roundImages || []).forEach(r => {
			if (r?.isObjectUrl && typeof r.img === 'string' && r.img.startsWith('blob:')) {
				try { URL.revokeObjectURL(r.img); } catch (e) {}
			}
			});
			state.roundImages = [];
		},

		// Optional helper if you want explicit per-file cleanup
		RemoveRoundImage(state, fileId) {
			const idx = state.roundImages.findIndex(x => x.fileId === fileId);
			if (idx === -1) return;
			const prev = state.roundImages[idx];
			if (prev?.isObjectUrl && typeof prev.img === 'string' && prev.img.startsWith('blob:')) {
			try { URL.revokeObjectURL(prev.img); } catch (e) {}
			}
			state.roundImages.splice(idx, 1);
		}
	},
	actions: {
		async saveFile({ commit, rootGetters }, data) {
			const payload = { ...(data || {}) };
			payload.customerId =
				payload.customerId ||
				rootGetters['user/getActiveCustomer']?.id ||
				rootGetters['user/getActiveBuilding']?.customerId ||
				rootGetters['user/getUser']?.customer?.id ||
				rootGetters['user/getUser']?.customerId;

			if (!payload.customerId) {
				commit('SetFileUploadSuccess', false);
				throw new Error('saveFile: missing customerId');
			}

			try {
				const result = await fileService.saveFile(payload);
				commit('SetFileUploadSuccess', true);
				return result;
			} catch (error) {
				commit('SetFileUploadSuccess', false);
				throw error;
			}
		},
		async getFile({ commit, dispatch }, id) {
			dispatch('appStore/setLoading', 1, { root: true });
			const data = await fileService.getFile(id).catch(err => console.log(err));
			dispatch('appStore/setLoading', -1, { root: true });

			if (data) {
				const toBase64 = file =>
					new Promise((resolve, reject) => {
						const reader = new FileReader();
						reader.readAsDataURL(file);
						reader.onload = () => resolve(reader.result);
						reader.onerror = error => reject(error);
					});

				commit('setFile', await toBase64(data));

				return await toBase64(data);
			}
		},
		async downloadFile({ dispatch }, id) {
			dispatch('appStore/setLoading', 1, { root: true });
			const data = await fileService
				.downloadFile(id)
				.catch(err => console.log(err));
			dispatch('appStore/setLoading', -1, { root: true });

			if (data) {
				return data;
			}
		},
		async getFiles({ state, commit, dispatch, rootGetters }, documentType) {
			dispatch('appStore/setLoading', 1, { root: true });
			let searchParams = { ...state.searchParams };
			searchParams.documentType = documentType;
			searchParams.customerId = rootGetters['user/getActiveCustomer']?.id;

			return await fileService
				.getFiles(searchParams)
				.then(data => {
					const paginationValues = data._pagination;

					if (state.searchParams.currentPage <= paginationValues.totalPages || paginationValues.totalPages == 0) {
						commit('SET_FILES', data);
						commit('SET_CURRENTPAGE', paginationValues.currentPage);
						commit('SET_TOTALCOUNT', paginationValues.totalCount);
						commit('SET_TOTALPAGES', paginationValues.totalPages);
					} else {
						commit('SET_CURRENTPAGE', paginationValues.totalPages);
						dispatch('getFiles');
					}

					dispatch('appStore/setLoading', -1, { root: true });
				})
				.catch(err => {
					console.log('Error getting files: ', err);
					dispatch('appStore/setLoading', -1, { root: true });
				});
		},
		async deleteFile({ dispatch }, file) {
			return await fileService
				.deleteFile(file.id)
				.then(() => {
					dispatch('getFiles', 1);
				})
				.catch(err => {
					console.log('Error deleting file: ', err);
				});
		},

		setCurrentPage({ commit }, currentPage) {
			commit('SET_CURRENTPAGE', currentPage);
		},
		setPageSize({ commit }, pageSize) {
			commit('SET_PAGESIZE', pageSize);
		},
		setTotalPages({ commit }, totalPages) {
			commit('SET_TOTALPAGES', totalPages);
		},
		setTotalCount({ commit }, totalCount) {
			commit('SET_TOTALCOUNT', totalCount);
		},
		async hydrateRoundImageFromOffline({ state, commit }, { fileId, mapFileLocalKey }) {
			// already in memory
			if (state.roundImages.some(x => x.fileId === fileId)) return true;

			if (!mapFileLocalKey) return false;

			const stored = await getOfflineFile(mapFileLocalKey);
			if (!stored?.blob) return false;

			const objectUrl = URL.createObjectURL(stored.blob);

			commit('AddRoundImage', {
			fileId,
			img: objectUrl,
			isObjectUrl: true
			});

			return true;
		}
	},
	getters: {
		files: state => {
			return state.files;
		},
		pagination: state => {
			return {
				currentPage: state.searchParams.currentPage,
				pageSize: state.searchParams.pageSize,
				totalPages: state.totalPages,
				totalCount: state.totalCount
			};
		},
		getRoundImage: (state) => (fileId) => {
			return state.roundImages.find(image => image.fileId === fileId)?.img ?? '';
		},
	}
};
